'use client';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { FixtureType } from '@/types/tournament.model';
import { cn } from '@/lib/utils';

export interface LongStructureFieldProps {
  value: FixtureType;
  onChange: (v: FixtureType) => void;
  disabled?: boolean;
}

const OPTIONS: { value: FixtureType; label: string; description: string }[] = [
  {
    value: FixtureType.GROUPS_AND_KNOCKOUTS,
    label: 'Zonas + playoffs',
    description: 'Fase de grupos semanal y cruces al final.',
  },
  {
    value: FixtureType.GROUPS_ONLY,
    label: 'Liga (sin playoffs)',
    description: 'Una sola tabla, todos contra todos.',
  },
];

/** Fixture structure selector for LONG tournaments. Same design as TournamentFormatField. */
export function LongStructureField({ value, onChange, disabled = false }: LongStructureFieldProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Estructura</CardTitle>
      </CardHeader>
      <CardContent>
        <RadioGroup
          value={value}
          onValueChange={(v) => onChange(v as FixtureType)}
          className="grid gap-3 sm:grid-cols-2"
          disabled={disabled}
        >
          {OPTIONS.map((opt) => {
            const isSelected = value === opt.value;
            return (
              <div
                key={opt.value}
                role="button"
                tabIndex={disabled ? -1 : 0}
                aria-pressed={isSelected}
                aria-disabled={disabled}
                onClick={() => !disabled && onChange(opt.value)}
                onKeyDown={(e) => {
                  if (disabled) return;
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onChange(opt.value);
                  }
                }}
                className={cn(
                  'flex flex-col gap-2 rounded-lg border-2 p-4 transition-all',
                  isSelected && !disabled
                    ? 'border-blue-500 bg-blue-50'
                    : disabled
                      ? 'cursor-not-allowed border-gray-100 bg-gray-50 opacity-60'
                      : 'cursor-pointer border-gray-200 bg-white hover:border-gray-300',
                )}
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem
                    value={opt.value}
                    id={`long-structure-${opt.value}`}
                    disabled={disabled}
                    tabIndex={-1}
                    className={
                      isSelected && !disabled
                        ? 'border-blue-600 text-blue-600 data-[state=checked]:border-blue-600'
                        : undefined
                    }
                  />
                  <Label
                    htmlFor={`long-structure-${opt.value}`}
                    className={cn(
                      'font-medium',
                      disabled ? 'cursor-not-allowed' : 'cursor-pointer',
                      isSelected && !disabled && 'text-blue-700',
                    )}
                  >
                    {opt.label}
                  </Label>
                </div>
                <p
                  className={cn(
                    'pl-6 text-xs',
                    isSelected && !disabled ? 'text-blue-700/85' : 'text-muted-foreground',
                  )}
                >
                  {opt.description}
                </p>
              </div>
            );
          })}
        </RadioGroup>
      </CardContent>
    </Card>
  );
}
